import ErrorBoundary from '@/components/ErrorBoundary';
import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { AudienceRequiredEmptyState } from '@/features/audiences/components/AudienceRequiredEmptyState';
import { Loading } from '@/components/loaders';
import { EmailTemplatePreviewModal } from '@/features/campaigns/EmailCampaign/templates/EmailTemplatePreviewModal';
import { HtmlEditorPortal } from '@/features/campaigns/EmailCampaign/templates/HtmlEditorPortal';
import type { EmailTemplateFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Email/TemplatePreview/EmailTemplatePreview';
import { createNamedTemplateEditFlowHandler, type NamedTemplateSelection } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/namedTemplateEditFlowHandler';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { MultiStepFlowForm } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/MultiStepFlowFormv2';
import { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useTemplateImageUpload } from '@/features/campaigns/hooks/useTemplateImageUpload';
import { useEmailChannelFlow } from '@/features/connectors/email';
import RouteConstants from '@/features/router/RouteConstants';
import { showToast } from '@/features/toasts/toastsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { Button } from '@/lib';
import { ToastType } from '@/models/toast';
import HtmlEditor from '@/modules/HTML_EDITOR';
import { useCompanyValues } from '@/providers';
import { useAppDispatch } from '@/reducers';
import { audiencerateApi } from '@/services/audiencerate';
import {
  PrepareConnectorFlowStepResponse,
  useCreateFlowInstanceMutation,
  useGetConnectorInstancesQuery,
  usePrepareConnectorFlowStepMutation,
} from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type EditPhase = 'metadata' | 'design';

const EMAIL_IMAGE_ACCEPT = ['image/png', 'image/jpeg', 'image/gif'];

export type EmailTemplateEditLocationState = {
  editSelection: NamedTemplateSelection;
};

type CreateTemplateOpts = {
  flowParams?: FlowFormData;
  flowMappings?: FlowFormData;
  fromFlowComplete?: boolean;
};

const readEditSelection = (state: unknown): NamedTemplateSelection | undefined => {
  const editSelection = (state as EmailTemplateEditLocationState | null | undefined)?.editSelection;
  if (!editSelection?.id && !editSelection?.name) return undefined;
  return editSelection;
};

const emailEditSelectionKey = (selection: NamedTemplateSelection | undefined): string => {
  if (!selection) return 'none';
  return `${selection.id ?? ''}:${selection.name ?? ''}`;
};

const EditInit = ({
  workspaceId,
  emailConnectorFlowId,
  connectorInstanceId,
  emailConnectorFlow,
}: {
  workspaceId: string;
  emailConnectorFlowId?: string;
  connectorInstanceId?: string;
  emailConnectorFlow: { connectorId?: string } | undefined;
}) => {
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled, hasAudience } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { t } = useTranslation('campaign');
  const appDispatch = useAppDispatch();
  const [prepareResult, setPrepareResult] = useState<PrepareStepResult | null>(null);
  const [prepareError, setPrepareError] = useState(false);
  const [prepareAttempt, setPrepareAttempt] = useState(0);
  const history = useHistory();
  const location = useLocation();
  const { upload: uploadTemplateImage } = useTemplateImageUpload();

  const editSelection = readEditSelection(location.state);
  const resolveStepAutoFill = useMemo(() => createNamedTemplateEditFlowHandler(editSelection), [editSelection]);
  const selectionKey = emailEditSelectionKey(editSelection);

  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [phase, setPhase] = useState<EditPhase>('metadata');
  const [formData, setFormData] = useState<FlowFormData>({});
  const [bodyHtml, setBodyHtml] = useState('');
  const [mappingData, setMappingData] = useState<FlowFormData>({});
  const [previewFormData, setPreviewFormData] = useState<EmailTemplateFormData | null>(null);

  const { data: instances } = useGetConnectorInstancesQuery({ workspaceId }, { skip: !workspaceId });
  const [createFlowInstance, { isLoading: isCreatingFlowInstance }] = useCreateFlowInstanceMutation();

  const reloadPrepare = useCallback(() => {
    setPrepareResult(null);
    setPrepareError(false);
    setPrepareAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    const fetchPrepareData = async () => {
      setPrepareError(false);
      try {
        const response = await prepareStep({
          workspaceId,
          flowId: emailConnectorFlowId,
          audienceId: audienceId!,
          stepId: 'step1',
          stepData: {},
          connectorInstanceId,
        }).unwrap();

        const translatedResponse = translateI18nValues(response, currentLanguage);
        const prepareResultResponse = translatedResponse?.configuration?.elements?.params;
        setPrepareResult(prepareResultResponse as PrepareStepResult);
      } catch {
        setPrepareError(true);
        setPrepareResult(null);
      }
    };
    if (!!connectorInstanceId && !!emailConnectorFlowId && !!audienceId) {
      void fetchPrepareData();
    }
  }, [audienceId, connectorInstanceId, emailConnectorFlowId, prepareStep, workspaceId, currentLanguage, prepareAttempt]);

  const handleEditTemplate = async (opts?: CreateTemplateOpts) => {
    const instance = instances?.find((inst) => inst.connectorId === emailConnectorFlow?.connectorId);

    if (!instance) {
      const message = String(t('noConnectorInstanceFound'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }

    if (!audienceId) {
      const message = String(t('missingAudience.toast'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: message,
        })
      );
      return { success: false, message };
    }

    const flowParams = {
      ...(opts?.flowParams ?? formData),
      template_body_html: (opts?.flowParams?.['template_body_html'] as string | undefined) ?? bodyHtml,
    };
    const flowMappings = opts?.flowMappings ?? mappingData;

    if (!String(flowParams['template_body_html'] ?? '').trim()) {
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: String(t('emailWorkspace.templates.emailPreviewBodyPlaceholder')),
        })
      );
      setPhase('design');
      return { success: false, message: 'Missing template body' };
    }

    const payload = buildFlowInstancePayload({
      workspaceId,
      connectorFlowId: emailConnectorFlowId!,
      connectorInstanceId: instance.id,
      audienceId,
      flowParams,
      flowMappings,
    });

    try {
      await createFlowInstance(payload).unwrap();
      appDispatch(
        showToast({
          title: String(t('whatsappTemplates.templateEditedSuccessfully')),
          type: ToastType.SUCCESS,
        })
      );
      appDispatch(audiencerateApi.util.invalidateTags(['Campaigns']));
      history.push(RouteConstants.emailTemplates);
      return { success: true };
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message ?? String(t('flowInstanceCreationFailed'));
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: errorMessage,
        })
      );
      return { success: false, message: errorMessage };
    }
  };

  const handleFlowComplete = (allParams: FlowFormData) => {
    setFormData(allParams);
    setMappingData({});
    setBodyHtml(typeof allParams['template_body_html'] === 'string' ? allParams['template_body_html'] : '');
    setPhase('design');
  };

  const handleEditorSave = ({ name, html }: { name: string; html: string }) => {
    if (!html.trim()) {
      appDispatch(
        showToast({
          type: ToastType.ERROR,
          title: String(t('emailWorkspace.templates.emailPreviewBodyPlaceholder')),
        })
      );
      return;
    }

    const nextFormData = { ...formData, template_name: name, template_body_html: html };
    setFormData(nextFormData);
    setBodyHtml(html);

    void handleEditTemplate({
      flowParams: nextFormData,
      flowMappings: {},
      fromFlowComplete: true,
    });
  };

  const isConnectorReady = !!connectorInstanceId && !!emailConnectorFlowId;
  const isPreparePending = hasAudience && isConnectorReady && !prepareResult && !prepareError;
  const isTemplateLoading = isAudienceLoading || isPreparePending;

  if (phase === 'design') {
    return (
      <>
        <HtmlEditorPortal>
          <HtmlEditor
            templateName={String(formData['template_name'] ?? '')}
            value={bodyHtml}
            onChange={setBodyHtml}
            onTemplateNameChange={(name) => setFormData((prev) => ({ ...prev, template_name: name }))}
            onClose={() => setPhase('metadata')}
            onPreview={(html) =>
              setPreviewFormData({
                ...(formData as EmailTemplateFormData),
                template_body_html: html,
              })
            }
            onSave={handleEditorSave}
            onUploadImage={(file) => uploadTemplateImage(file, EMAIL_IMAGE_ACCEPT)}
            disabled={isCreatingFlowInstance}
          />
        </HtmlEditorPortal>
        {previewFormData ? <EmailTemplatePreviewModal formData={previewFormData} onClose={() => setPreviewFormData(null)} /> : null}
      </>
    );
  }

  if (isAudienceLoading) {
    return <Loading />;
  }

  if (isAudienceSettled && !hasAudience) {
    return <AudienceRequiredEmptyState />;
  }

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-3xl font-semibold text-gray-900'>{t('whatsappTemplates.editTemplatesHub')}</h2>
      </div>
      <ErrorBoundary>
        {prepareResult ? (
          <MultiStepFlowForm
            key={`${emailConnectorFlowId}-${audienceId}-${selectionKey}`}
            id='email-template-edit-workaround-form'
            showTemplatePreview
            templatePreviewVariant='email'
            htmlBodyFieldMode='external'
            lastStepActionLabel={t('emailWorkspace.templates.continueToEmailDesigner')}
            audienceId={audienceId!}
            connectorInstanceId={connectorInstanceId!}
            connectorFlowId={emailConnectorFlowId!}
            paramsConfig={prepareResult}
            resolveStepAutoFill={resolveStepAutoFill}
            isInAction={isCreatingFlowInstance}
            onComplete={handleFlowComplete}
          />
        ) : (
          <div className='flex flex-col items-start gap-3'>
            <p className='text-sm text-gray-600'>
              {isTemplateLoading && !prepareError
                ? t('emailWorkspace.templates.templateLoadingMessage')
                : t('emailWorkspace.templates.failedToLoadTemplate')}
            </p>
            {prepareError ? (
              <Button intent='secondary' onClick={() => reloadPrepare()} text={t('emailWorkspace.templates.tryAgain')} />
            ) : null}
          </div>
        )}
      </ErrorBoundary>
    </div>
  );
};

export const EmailTemplateEditWorkaround = () => {
  const { id: workspaceId } = useWorkspace();

  const { flow: emailConnectorFlow, connectorInstanceId, isLoading } = useEmailChannelFlow('templateEdit');
  const emailConnectorFlowId = emailConnectorFlow?.id;

  if (isLoading) {
    return <Loading />;
  }

  return (
    <EditInit
      workspaceId={workspaceId}
      emailConnectorFlowId={emailConnectorFlowId}
      connectorInstanceId={connectorInstanceId}
      emailConnectorFlow={emailConnectorFlow}
    />
  );
};
